(ns frontend.view.login.events
    (:require
      [clojure.string :as string]
      [cognitect.transit :as t]
      [comms.sente :as sente]
      [common.sync-progress :as sync-progress]
      [common.util :as cu]
      [frontend.account :as account]
      [frontend.actions.common :as common-actions]
      [frontend.actions.login :as login-actions]
      [frontend.actions.role :as role-actions]
      [frontend.browser-utils :as bu]
      [frontend.db :as app-db]
      [frontend.device-id :as device-id]
      [frontend.domain.oppo.grid-settings :as oppo-grid-settings]
      [frontend.local-storage :as ls]
      [frontend.logging :as logging]
      [frontend.routing :as routing]
      [frontend.shared.crypto :as shared-crypto]
      [frontend.shared.state :as shared-state]
      [frontend.state.current-session :as session]
      [frontend.state.updates :as state-updates]
      [frontend.utils :as fu]
      ["localforage" :as localforage]
      [frontend.view.login.db :as db]
      [frontend.zap-time :as zap-time]
      [promesa.core :as p]
      [ui.dom-globals :as dg]))

(defonce crypto-debug (atom {}))

;; https://app.zapflow.com/plugin/v1/mcp
;; https://stenosed-unwelted-nicki.ngrok-free.dev/plugin/v1/mcp
;;
;; OAuth return URL handling
;; After any successful login the frontend checks with the backend whether
;; an OAuth authorize flow was pending (stored server-side keyed by cookie).
;; This works for all login paths: password, MFA, and SSO.

(defn- navigate-after-login!
  "After successful login, check the backend for a pending OAuth return URL
   (stored on the session cookie when an unauthenticated user was redirected
   to login from /plugin/v1/oauth/authorize). Falls through to landing-page-fn
   on error or when no pending return exists."
  [landing-page-fn]
  (-> (js/fetch "/plugin/v1/oauth/pending-return" #js {:method "GET" :credentials "same-origin"})
      (.then (fn [resp] (.json resp)))
      (.then (fn [data]
               ;; String-keyed access: (.-return_url data) gets the property name
               ;; munged under :advanced compilation, so on production builds it
               ;; read undefined and the OAuth redirect silently fell through to
               ;; the landing page even though the backend returned the URL.
               (let [return-url (aget data "return_url")]
                 ;; TODO: temporary diagnostic — remove once verified on staging.
                 (js/console.log "[oauth pending-return] data:" data
                                 "| (aget data \"return_url\"):" return-url
                                 "| (.-return_url data):" (.-return_url data))
                 (if return-url
                   (aset js/window.location "href" return-url)
                   (landing-page-fn)))))
      (.catch (fn [_] (landing-page-fn)))))


(defn on-change!
  [field e]
  (let [value (dg/event-target-value e)]
    (swap! db/state assoc field value)
    (when (string/blank? value)
      (swap! db/state assoc :login-status :ok))))

(defn notify-worker-to-start-sync
  [{:keys [iv
           native-crypto-key
           tenant
           account
           role-id
           target
           last-known-update
           last-permissions-ts
           last-tenant-plan-ts
           data-schema-version
           reset-sync?]
    :as   _ctx}]
  (let [{:keys [worker env]} @app-db/app-state
        {:keys [token]} @app-db/session]
    (if-not token
      (logging/log "Notify workder to start sync - even if we don't have token !!")
      (.postMessage worker
                    (clj->js
                      {:action     (cond reset-sync?
                                         "reset-sync->worker"
                                         :else
                                         "start-sync->worker")
                       :parameters {:url                 (bu/websocket-url)
                                    :env                 env
                                    :iv                  iv
                                    :native-crypto-key   native-crypto-key
                                    :reset-sync?         reset-sync?
                                    :last-known-update   last-known-update
                                    :last-permissions-ts last-permissions-ts
                                    :last-tenant-plan-ts last-tenant-plan-ts
                                    :data-schema-version data-schema-version
                                    :tenant-id           (:db/id tenant)
                                    :account-id          (:db/id account)
                                    :role-id             role-id
                                    :target-id           (:db/id target)
                                    :token               token
                                    :device-id           (device-id/device-id)}})))))

(declare clear-indexeddb-and-generate-new-keys)

(defn start-worker-sync!
  [ctx
   last-known-update
   last-permissions-ts
   last-tenant-plan-ts
   indexeddb-data-schema-version]

  (notify-worker-to-start-sync
    (assoc ctx
      :last-known-update last-known-update
      :last-permissions-ts last-permissions-ts
      :last-tenant-plan-ts last-tenant-plan-ts
      :data-schema-version indexeddb-data-schema-version)))

(defn clear-db-and-start-sync!
  [ctx]
  (println "Clear indexedDb")
  (shared-state/clear-indexeddb ctx identity identity)
  (start-worker-sync! ctx 0 0 0 0))

(defn process-valid-cached-entities-and-start-sync!
  [{:keys [stored-entities last-known-update last-permissions-ts last-tenant-plan-ts] :as ctx}]
  (println "Processed valid cache " last-known-update " > " last-permissions-ts " > " last-tenant-plan-ts)
  (let [entities (vals stored-entities)]
    (state-updates/process-updates
      {:reset-session!  account/reset-session
       :updates         {:sync-progress sync-progress/COMPLETE
                         :entities      entities}
       :deserialize?    false
       :from-indexeddb? true})
    #_(logging/debug "Processed loaded cache " (- (zap-time/now) decrypted-ts) "ms")
    ;; If we want to show the spinner until my-account is received from delta-sync:
    ;;(swap! session/session dissoc :account-data-loaded?)
    ;; Hack to force things visible after reading indexeddb but
    ;; before we get data from delta-sync (TODO rethink this):
    (swap! session/session assoc :account-data-loaded? true)
    (fu/timeout
      20
      (fn [_]
        (println "Valid cache -> start sync")
        (start-worker-sync!
          ctx
          last-known-update
          last-permissions-ts
          last-tenant-plan-ts
          @app-db/data-schema-version)))))

(defn process-decrypted-cached-data
  [{:keys [read-ts] :as ctx} decrypted-str]
  (let [decrypted-ts (zap-time/now)
        reader (t/reader :json)
        deserialized (t/read reader decrypted-str)
        old-data-schema-version (:data-schema-version deserialized)
        last-known-update (:last-known-update deserialized)
        last-permissions-ts (:last-permissions-ts deserialized)
        last-tenant-plan-ts (:last-tenant-plan-ts deserialized)
        ;;_ (swap! app-db/app-state assoc :last-known-update last-known-update)
        _ (logging/debug "Decrypted db in" (- decrypted-ts read-ts))
        ;;_ (prn ">! last-known-update read from indexeddb" last-known-update)
        clear-indexeddb? (not= old-data-schema-version @app-db/data-schema-version)
        stored-entities (when-not clear-indexeddb?
                          (:entities deserialized))
        end-ts (zap-time/now)]
    (if clear-indexeddb?
      (clear-db-and-start-sync! ctx)
      (fu/timeout 5                                         ;; Timeout here to allow UI thread draw images
                  #(process-valid-cached-entities-and-start-sync!
                     (assoc ctx
                       :stored-entities stored-entities
                       :last-known-update last-known-update
                       :last-permissions-ts last-permissions-ts
                       :last-tenant-plan-ts last-tenant-plan-ts
                       :end-ts end-ts
                       :decrypted-ts decrypted-ts))))))

(defn load-indexed-db-state
  [{:keys [iv native-crypto-key tenant account target role-id] :as ctx}]
  (let [data-key
        (shared-state/indexeddb-data-path (:db/id tenant) (:db/id account) role-id (:db/id target))
        start-ts (zap-time/now)]
    (-> (.getItem localforage data-key)
        (p/then
          (fn [resp]
            (let [read-ts (zap-time/now)]
              (logging/debug "Read from indexeddb" (- read-ts start-ts))
              (if-not resp
                (clear-indexeddb-and-generate-new-keys ctx)
                (shared-crypto/decrypt
                  {:context        js/window
                   :decryption-key native-crypto-key
                   :iv             (.-buffer (js/Uint8Array.from iv))
                   :encrypted-data resp
                   :on-error!      (fn [err]
                                     (logging/debug "Error decrypting indexeddb:" err)
                                     (println "Could not decrypt cache -> start FULL sync")
                                     (shared-state/clear-indexeddb ctx identity identity)
                                     (fu/timeout 20 (partial start-worker-sync! ctx 0 0)))
                   :on-success!    (partial process-decrypted-cached-data
                                            (assoc ctx
                                              :start-ts start-ts
                                              :read-ts read-ts))})))))
        (p/catch (fn [_err]
                   (clear-indexeddb-and-generate-new-keys ctx))))))

(defn save-new-crypto-key-and-start
  [{:keys [role-id] :as ctx} ^CryptoKey c-key]
  (-> js/window
      .-crypto
      .-subtle
      (.exportKey "raw" c-key)
      (p/then
        (fn [exported-key]
          (let [raw-key (vec (js/Uint8Array. exported-key))
                iv (vec (shared-crypto/random-values js/window))]
            (swap! crypto-debug
                   assoc
                   :iv iv
                   :crypto-key raw-key)
            (role-actions/*->set-encryption-data
              {:crypto-key raw-key
               :iv         iv
               :role-id    role-id}
              (fn [_resp]
                (notify-worker-to-start-sync (merge (dissoc ctx
                                                            :last-known-update
                                                            :last-permissions-ts
                                                            :last-tenant-plan-ts)
                                                    {:iv                iv
                                                     :native-crypto-key c-key})))
              (fn [_err]
                (logging/error "Failed to save encryption keys")
                (notify-worker-to-start-sync (dissoc ctx
                                                     :native-crypto-key
                                                     :last-known-update
                                                     :last-permissions-ts
                                                     :last-tenant-plan-ts)))))))
      (p/catch (fn [err]
                 (logging/error "Failed to export key " err)))))

(defn generate-new-encryption-keys-and-start
  [ctx]
  (js/console.log "Create cache encryption keys")
  (-> js/window
      .-crypto
      .-subtle
      (.generateKey shared-crypto/encryption-algorithm-js
                    true
                    shared-crypto/encryption-modes-js)
      (p/then (fn [^CryptoKey c-key]
                (save-new-crypto-key-and-start ctx c-key)))
      (p/catch (fn [err]
                 (logging/error "Failed to generate key " err)
                 (notify-worker-to-start-sync (dissoc ctx
                                                      :native-crypto-key
                                                      :last-known-update
                                                      :last-permissions-ts
                                                      :last-tenant-plan-ts))))))

(defn clear-indexeddb-and-generate-new-keys
  [ctx]
  (shared-state/clear-indexeddb
    ctx
    (partial generate-new-encryption-keys-and-start ctx)
    (fn [_err]
      (generate-new-encryption-keys-and-start ctx))))

(defn reset-sync!
  [ctx]
  (if @session/use-indexeddb?
    (clear-indexeddb-and-generate-new-keys ctx)
    (notify-worker-to-start-sync (dissoc ctx
                                         :last-known-update
                                         :last-permissions-ts
                                         :last-tenant-plan-ts))))

(defn import-crypto-key-and-start
  [{:keys [crypto-key] :as ctx}]
  (-> js/window
      .-crypto
      .-subtle
      (.importKey "raw"
                  (js/Uint8Array.from crypto-key)
                  shared-crypto/encryption-algorithm-name
                  true
                  shared-crypto/encryption-modes-js)
      (p/then (fn [^CryptoKey c-key]
                (load-indexed-db-state (assoc ctx :native-crypto-key c-key))))
      (p/catch (fn [err]
                 (logging/error "Failed to import key " err)
                 (clear-indexeddb-and-generate-new-keys ctx)))))

(defn load-data-and-updates
  [{:keys [crypto-key] :as ctx}]
  ;; Re-handshake before any of this loads. Sente resolves a connection's uid
  ;; once, in user-id-fn, and the socket is opened at boot - before the user has
  ;; authenticated - so the server finds no token on the cookie and stamps it
  ;; `:sente/nil-uid` for good. backend.domain.updates/open-stream refuses those
  ;; streams, which is what made an on-demand entity fetch (the fund NEV always
  ;; takes that path) fail with access-denied and render "This page is not
  ;; available" until the user happened to refresh. Every caller of this fn has
  ;; just put a token on the cookie, which is exactly when a fresh handshake can
  ;; resolve a real account id. Streams opened before this re-establish
  ;; themselves: resubscribe-active-streams! runs when the socket reopens.
  (sente/reconnect!)
  (cond
    (not @session/use-indexeddb?)
    ;;guests, zadmins, sys-admins: always full intial-dataset:
    (do
      (println "Do not use cache")
      (notify-worker-to-start-sync (assoc
                                     ctx
                                     :last-known-update
                                     0)))

    crypto-key
    (import-crypto-key-and-start ctx)

    :else
    (generate-new-encryption-keys-and-start ctx)))

;; TODO: cleanup set-tenant to take only selected role-id and no other params
(defn set-tenant!
  [target-id target-account-id role-id tenant-id]
  (logging/performance "Set tenant ")
  (let [data {:role-id    role-id
              :account-id target-account-id
              :tenant-id  tenant-id
              :target-id  target-id}
        on-success! (fn [res]
                      (logging/performance "Tenant set ")
                      (swap! db/state dissoc :loading)
                      @db/state
                      (let [body (:body res)
                            {:keys [tenant iv crypto-key role role-id account target token]} body]
                        (swap! db/state assoc :login-status :ok)
                        (account/update-session
                          :token token
                          :email (:account/email account)
                          :account account
                          :intercom-hash (:intercom-hash body)
                          :tenant tenant
                          :role role
                          :role-id role-id
                          :target target)
                        (load-data-and-updates {:account    account
                                                :tenant     tenant
                                                :role-id    role-id
                                                :role       role
                                                :iv         iv
                                                :crypto-key crypto-key
                                                :target     target})

                        ;; CLEAR Local Storage :TODO : XXXX - Vataja why do we do this again - should this be done for all entity type grids ?
                        (ls/set-in (oppo-grid-settings/ls-path) nil)

                        (navigate-after-login!
                          (fn []
                            (when (or (-> @app-db/view
                                          :view-id
                                          (routing/no-login-required?))
                                      (and (session/is-mobile?)
                                           (not (session/is-guest?))))
                              (let [landing-page
                                    (if-not (:account/current-permissions @session/account)
                                      :oppos-grid       ;; first login after setting pw
                                      (cond (session/has-module-access? @session/account :module/dealflow)
                                            :oppos-grid
                                            (session/has-module-access? @session/account :module/fundraising)
                                            :fundraising-opportunities-grid
                                            (session/has-module-access? @session/account :module/portfolio)
                                            :holdings-grid
                                            :else
                                            :companies-grid))]
                                (routing/set-view! [landing-page])))))))
        on-error! (fn [e]
                    (logging/performance "Tenant set failed ")
                    (swap! db/state dissoc :loading)
                    @db/state
                    (logging/error "set-tenant! failed" e)
                    (account/update-session :tenant nil
                                            :intercom-hash nil
                                            :target nil
                                            :role nil
                                            :role-id nil))]
    (swap! db/state assoc :loading true)
    @db/state
    (when target-id
      (login-actions/*<-set-role data on-success! on-error!))))

(defn set-tenant-with-mfa!
  ([target-id target-account-id role-id tenant-id mfa]
   (set-tenant-with-mfa! target-id target-account-id role-id tenant-id mfa nil))
  ([target-id target-account-id role-id tenant-id mfa-1 mfa-2]
   (let [mfa-1 (-> mfa-1
                   str
                   (string/replace #"[^0-9]" "")
                   fu/str->int)
         mfa-2 (when mfa-2
                 (-> mfa-2
                     str
                     (string/replace #"[^0-9]" "")
                     fu/str->int))
         data (merge {:role-id    role-id
                      :account-id target-account-id
                      :tenant-id  tenant-id
                      :target-id  target-id}
                     (when (and (number? mfa-1)
                                (> mfa-1 0))
                       {:mfa mfa-1})
                     (when (and (number? mfa-2)
                                (> mfa-2 0))
                       {:mfa-2 mfa-2})
                     (when-not (nil? mfa-2)
                       {:setup-mfa? true}))
         on-success!
         (fn [res]
           (let [body (:body res)
                 {:keys [tenant iv crypto-key role role-id account target token]} body]
             (swap! db/state assoc :login-status :ok)
             (swap! db/state dissoc :mfa-data :mfa-setup-data :mfa :mfa-1 :mfa-2)
             (account/update-session
               :account account
               :intercom-hash (:intercom-hash body)
               :tenant tenant
               :token token
               :role role
               :role-id role-id
               :target target)
             (load-data-and-updates {:account    account
                                     :tenant     tenant
                                     :role-id    role-id
                                     :role       role
                                     :iv         iv
                                     :crypto-key crypto-key
                                     :target     target})


             ;; CLEAR Local Storage
             (ls/set-in (oppo-grid-settings/ls-path) nil)

             (navigate-after-login!
               (fn []
                 (when (-> @app-db/view
                           :view-id
                           (routing/no-login-required?))
                   (let [landing-page
                         (cond (session/has-module-access? @session/account :module/dealflow)
                               :oppos-grid
                               (session/has-module-access? @session/account :module/fundraising)
                               :fundraising-opportunities-grid
                               (session/has-module-access? @session/account :module/portfolio)
                               :holdings-grid
                               :else
                               :companies-grid)]
                     (routing/set-view! [landing-page])))))))
         on-error! (fn [e]
                     (let [msg (-> e
                                   ex-data
                                   :message)]
                       (logging/error "set-tenant! failed" e)
                       (if (some #{msg} [:mfa-does-not-match :invalid-mfa-codes])
                         (swap! db/state assoc :mfa-error? true)
                         (account/update-session :tenant nil
                                                 :intercom-hash nil
                                                 :target nil
                                                 :role nil
                                                 :role-id nil))))]
     (when target-id
       (swap! db/state dissoc :mfa-error?)
       (login-actions/*<-set-role data on-success! on-error!)))))

(defn show-mfa!
  [target-id target-account-id role-id tenant-id target-name]
  (let [mfa-data {:role-id     role-id
                  :account-id  target-account-id
                  :tenant-id   tenant-id
                  :tenant-name target-name
                  :target-id   target-id}]
    (swap! db/state assoc :mfa-data mfa-data)))

(defn show-mfa-setup!
  [target-id target-account-id role-id tenant-id target-name]
  (swap! db/state assoc :loading true)                      ;;or :login-status :pending
  (login-actions/*>-setup-mfa
    {:account-id target-account-id
     :tenant-id  tenant-id}
    (fn [response]
      (let [body (-> response
                     :body)
            {:keys [image-data secret-key]} body
            mfa-data {:role-id     role-id
                      :account-id  target-account-id
                      :tenant-id   tenant-id
                      :tenant-name target-name
                      :target-id   target-id
                      :image-data  image-data
                      :secret-key  secret-key}]
        (swap! db/state assoc :mfa-setup-data mfa-data)))
    (fn [err]
      (logging/error "Failed to setup MFA " err))))

(defn hide-mfa-error!
  []
  (swap! db/state dissoc :mfa-error?))

(defn roles-loaded!
  [set-status-to-render-login! {roles :body}]
  (let [non-workspace-roles (some->> roles
                                     (cu/remove-by :target-type :entity.type/workspace))
        roles (if (seq non-workspace-roles)
                ;; workspace target is associated in the backend with a hardcoded link
                non-workspace-roles
                roles)]
    ;; if only one role, login immediately
    (if (= (count roles) 1)
      (let [role (first roles)
            role-id (:db/id role)
            account-id (->> role
                            :target-account
                            :db/id)
            target-role (:role role)
            {:keys [target-id tenant-id custom-login-path sso-enforced target-name target-type ask-mfa? setup-mfa?]} role]
        (cond
          (and (not= :role/sys-admin target-role)
               (coll? sso-enforced)
               (some #{:azure} sso-enforced)
               custom-login-path)
          (aset js/window.location "href" (str "/login/sso/azure/" custom-login-path))
          ask-mfa?
          (do (set-status-to-render-login!)
              (swap! session/tenant assoc :tenant/custom-login-path custom-login-path)
              (deref session/tenant)
              (show-mfa! target-id account-id role-id tenant-id target-name))
          setup-mfa?
          (do (set-status-to-render-login!)
              (swap! session/tenant assoc :tenant/custom-login-path custom-login-path)
              (deref session/tenant)
              (show-mfa-setup! target-id account-id role-id tenant-id target-name))
          (= target-type :entity.type/company)
          (set-tenant! target-id account-id role-id tenant-id)
          (= target-type :entity.type/oppo)
          (set-tenant! target-id account-id role-id tenant-id)
          (= target-type :entity.type/workspace)
          (set-tenant! target-id account-id role-id tenant-id)
          :else
          (set-tenant! target-id account-id role-id target-id)))
      (do (set-status-to-render-login!)
          (swap! db/state assoc :account-roles roles)))))

(defn get-roles!
  [set-status-to-render-login!]
  (let [_ (swap! db/state dissoc :account-roles)
        on-success! (fn [resp]
                      (roles-loaded! set-status-to-render-login! resp))
        on-error! (fn [_]
                    (swap! db/state assoc :account-roles []))]
    (login-actions/*<-get-roles {} on-success! on-error!)))

;;
;; ==========================================================================
;; Init session using token:
;; ==========================================================================
;;

(defn validate-authenticated-session
  [device-id]
  (let [data {:device-id device-id}
        on-success! (fn [args]
                      (let [body (:body args)
                            {:keys [token email]} body
                            update-state! (fn []
                                            (swap! db/state assoc :login-status :ok) ;; TODO - can this be cleaned up, seems like not used
                                            (account/update-session
                                              :email email
                                              :token token
                                              :intercom-hash nil)
                                            nil)]
                        (get-roles! update-state!)))
        on-error! (fn [e]
                    (logging/info "Logout >> VALIDATE AUTH FAILED")
                    (logging/info e)
                    (logging/error "validate authenticated session: failed" e)
                    (account/reset-session))]
    (login-actions/*<-validate-authentication data on-success! on-error!)))

(defn validate-saved-session
  [token device-id]
  (let [data {:token     token
              :device-id device-id}
        on-success!
        (fn [args]
          (let [body (:body args)
                {:keys [account token role-id iv crypto-key target tenant role]} body
                ctx {:account    account
                     :tenant     tenant
                     :role-id    role-id
                     :role       role
                     :iv         iv
                     :crypto-key crypto-key
                     :target     target}]
            (swap! db/state assoc :login-status :ok)
            (account/update-session :account account
                                    :token token
                                    :intercom-hash (:intercom-hash body)
                                    :target target
                                    :tenant tenant
                                    :role role
                                    :role-id role-id
                                    :switching-tenant? false)
            (load-data-and-updates ctx)
            (navigate-after-login!
              (fn []
                (when (session/is-mobile?)
                  (routing/set-view! [:oppos-grid]))))))
        on-error! (fn [e]
                    (logging/info "Logout >> VALIDATE SAVED SESSION FAILED")
                    (logging/info e)
                    (logging/error "validate-saved-session: failed" e)
                    (account/reset-session))]
    (login-actions/*<-validate-token data on-success! on-error!)))

(defn force-reload
  []
  ;;TODO also force full-initial-dataset?
  (logging/log "Reload page on init!")
  (set! (.-pathname js/window.location) "/"))


;; Login workflow after page reloads calls this.
;; Existing token will be validated in the backend, if it
;; exists.
(defn init!
  [url-token on-login!]
  (let [device-id (device-id/device-id)
        config-keys [:inbound-email-domain :intercom-id :heap-io-id :ag-grid-license-key
                     :docusign-integration-key
                     :nylas/app-id :server-url
                     :version :env :data-schema-version]
        on-success! (fn [response]
                      (let [config (:body response)
                            {:keys [token version]} config
                            old-version @app-db/version
                            config-updates (select-keys config config-keys)]
                        (when (seq config-updates)
                          (swap! app-db/app-state merge config-updates))
                        (cond
                          (and old-version
                               (not= old-version version))
                          ;; Reload changed application code
                          (force-reload)

                          (routing/login-required? (:view-id (routing/view)))
                          (let [resolved-token (or url-token token)]
                            (if (empty? resolved-token)
                              (validate-authenticated-session device-id)
                              (validate-saved-session resolved-token device-id))))
                        (on-login!)))
        on-error! (partial logging/error "Error loading config")]
    (common-actions/*<-app-config nil on-success! on-error!)))
